import CustomerSimplifiedForm from '@/components/Customers/CustomerSimplifiedForm';
import WorkorderImportAreaItemsModal from '@/components/Movements/WorkOrders/WorkorderImport/WorkorderImportAreaItemsModal';
import WorkorderImportForm from '@/components/Movements/WorkOrders/WorkorderImport/WorkorderImportForm';
import WorkorderImportInfoModal from '@/components/Movements/WorkOrders/WorkorderImport/WorkorderImportInfoModal';
import WorkorderImportItems from '@/components/Movements/WorkOrders/WorkorderImport/WorkorderImportItems';
import WorkorderImportModal from '@/components/Movements/WorkOrders/WorkorderImport/WorkorderImportModal';
import WorkorderImportSummary from '@/components/Movements/WorkOrders/WorkorderImport/WorkorderImportSummary';
import { DefaultRouteTabs, Group, SaveButton } from '@/components/_common';
import CustomersSelectedModal from '@/components/_common/CustomersSelectedModal';
import VehicleFormWrapper from '@/components/_common/_VehicleModal/VehicleFormWrapper';
import { parseAccessoryId } from '@/components/_common/_VehicleModal/getAccessoryId';
import { useWorkorderImportCreate } from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.hooks';
import { WorkorderImportIsurer } from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.types';
import { useDisclosure } from '@/hooks';
import { PageProps } from '@/types/PageProps';
import { applyMask, removeMask } from '@/utils/mask';
import { phoneMask } from '@/utils/mask/commonMasks';
import { DateTimeFormat } from '@/utils/moment';
import {
  Button,
  Col,
  Divider,
  Empty,
  Form,
  message,
  Row,
  Typography,
} from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaUserPlus } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useImportDataToForms } from './useWorkorderImportData';
import { useCustomerAddressesMutation } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';
import { useCustomerContactsMutation } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { Contact, ContactType } from '@/core/domain/people/_shared/Contact';
import { useCustomerGeneralMutation } from '@/core/domain/people/Customers/Customers.hooks';
import { workorderRoutes } from '../route';
import { uploadArchive } from '@/core/domain/archive/Archive/Archive.service';

const { Title } = Typography;

export type WorkorderImportInfo = {
  importAs: 0 | 1 | 2;
  workordrId?: number;
  complementId?: number;
};

const keyDate = new Date().toString();

const WorkOrderImportPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const history = useHistory();

  const [importInfo, setImportInfo] = useState<WorkorderImportInfo>();
  const [selectedCustomerId, setSelectedCustomerId] = useState<number>();

  const [modalOpened, modalAction] = useDisclosure(false);
  const [modalAreaOpened, modalAreaAction] = useDisclosure(false);
  const [modalInfoOpened, modalInfoAction] = useDisclosure(false);
  const [modalCustomerOpened, modalCustomerAction] = useDisclosure(false);

  const workorderImportCreate = useWorkorderImportCreate();
  const customerGeneral = useCustomerGeneralMutation();
  const customerContact = useCustomerContactsMutation();
  const customerAddress = useCustomerAddressesMutation();

  const {
    importData,
    setImportData,
    forms: { generalForm, customerForm, vehicleForm },
  } = useImportDataToForms();

  const generalFormValues = Form.useWatch([], generalForm);
  const customerAddressForm = Form.useWatch(['address'], customerForm);

  const hasItemsToImport = !!generalFormValues?.items?.length;

  const hasCustomerRegistered = !!importData?.data?.customer?.id;

  const getContactValue = (
    contacts: Contact[],
    type: ContactType,
  ): string | undefined => {
    const contact = contacts.find(({ detailList }) =>
      detailList.find(({ contactType }) => contactType === type),
    );

    return contact?.detailList.find(({ contactType }) => contactType === type)
      ?.value;
  };

  useEffect(() => {
    if (!importData) {
      modalAction.open();
    } else if (importData?.data?.customer?.id) {
      setSelectedCustomerId(importData?.data?.customer?.id);
    }
  }, [importData]);

  useEffect(() => {
    if (importData) {
      if (importData?.data?.existingOpenWorkOrder) {
        modalInfoAction.open();
      }
    }
  }, [importData]);

  function validateFields() {
    return Promise.all([
      generalForm.validateFields(),
      vehicleForm.validateFields(),
      customerForm.validateFields(),
    ]);
  }

  async function handleSubmit() {
    try {
      const [generalValues, vehicleValues, customerValues] =
        await validateFields();

      const insurer: WorkorderImportIsurer =
        importData?.data.insurer &&
        importData?.data.insurer?.id === generalValues?.insurerId
          ? {
              id: generalValues?.insurerId,
              name: generalValues?.insurerName,
              address: importData.data.insurer.address,
              documentIdentifier: importData.data.insurer.documentIdentifier,
            }
          : {
              id: generalValues?.insurerId,
              name: generalValues?.insurerName,
            };

      let image = vehicleValues.image;

      if (!!vehicleValues.imageUpload) {
        const result = await uploadArchive({
          file: [vehicleValues.imageUpload as Blob],
          isPublic: false,
        });

        image = result.filePath;
      }

      const createResult = await workorderImportCreate.mutateAsync({
        ...generalValues,
        ...importInfo,
        items: generalValues?.items.map((item) => ({
          isProvided: item.isProvided,
          type: item.type,
          register: item.register,
          itemId: item.itemId,
          itemName: item.description,
          isInternal: item.isInternal,
          quantity: Number(item.quantity),
          unitPrice: item.unitPrice,
          unitCost: item.unitCost,
          discount: item.discount,
          subAreaId: item.subAreaId,
          manufacturingCode: item.code,
          subGroupId: item.subGroupId,
          manufacturerId: item.manufacturerId,
          isTypedPart: item.isTypedPart,
        })),
        insurer,
        workOrderType: generalValues?.type,
        customer: {
          name: customerValues?.name,
          address: {
            cityId: customerValues?.address?.cityId,
            city: customerValues?.address?.city,
            complement: customerValues?.address?.complement,
            district: customerValues?.address?.district,
            number: customerValues?.address?.number,
            state: customerValues?.address?.state,
            street: customerValues?.address?.street,
            zipCode: customerValues?.address?.zipCode,
          },
          cellPhone: customerValues?.phone,
          phoneNumber: customerValues?.phone,
          documentNumber: customerValues?.cpfCnpj,
          id: selectedCustomerId || undefined,
          email: customerValues?.email,
          enableLGPD: customerValues?.enableLGPD,
          fantasyName: customerValues?.fantasyName,
          municipalRegistration: customerValues?.municipalRegistration,
          retentionIRPJorCSLL: customerValues?.retentionIRPJorCSLL,
          retentionISS: customerValues?.retentionISS,
        },
        customerType: generalValues?.customerType,
        insurerDeductibleAmount: generalValues?.insurerDeductibleAmount,
        entryDate: generalValues?.entryDate
          ? generalValues?.entryDate.format(DateTimeFormat)
          : undefined,
        estimatedLeaveDate: generalValues?.estimatedLeaveDate
          ? generalValues?.estimatedLeaveDate.format(DateTimeFormat)
          : undefined,
        vehicle: {
          id:
            removeMask(vehicleValues.licensePlate) ===
            removeMask(importData?.data?.vehicle?.licensePlate)
              ? importData?.data?.vehicle?.id
              : undefined,
          licensePlate: vehicleValues.licensePlate,
          brandId: vehicleValues.brandId,
          modelId: vehicleValues.modelId,
          fuelId: vehicleValues.fuelId,
          manufactureYear: Number(vehicleValues?.yearFabrication),
          modelYear: Number(vehicleValues?.yearModel),
          color: vehicleValues?.color?.name,
          colorId: vehicleValues.colorId,
          chassis: vehicleValues?.chassi,
          averageMileagePerMonth: vehicleValues?.km,
          accessories: vehicleValues?.accessories?.map((accessory) =>
            parseAccessoryId(accessory),
          ),
          cylinders: vehicleValues?.cylinders,
          engineCapacity: vehicleValues?.engineCapacity,
          engineName: vehicleValues?.engineName,
          engineTechnicalCode: vehicleValues?.engineTechnicalCode,
          horsePower: vehicleValues?.horsePower
            ?.toString()
            ?.replaceAll(',', ';'),
          image,
          release: vehicleValues?.release,
          valves: vehicleValues?.valves,
        },
      });

      if (createResult.workOrderComplementId && createResult.workOrderId) {
        history.push(
          `/app/movements/workOrders/${createResult.workOrderId}/complements/${createResult.workOrderComplementId}/generalInfo`,
        );
      } else {
        history.push(`/app/movements/workOrders`);
      }

      modalAreaAction.close();
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any).values) {
        message.warning('Preencha todos os campos.');
      }
    }
  }

  function resetFields() {
    generalForm.resetFields();
    customerForm.resetFields();
    vehicleForm.resetFields();
  }

  const newRoute = {
    ...route.parent,
    routes: [
      route.parent,
      ...route.parent.routes.filter((route) =>
        workorderRoutes.includes(route.key),
      ),
    ],
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        route={newRoute}
        permissions={route.permissions}
        extra={
          !!importData ? (
            <Button onClick={modalAction.open}>Nova importação</Button>
          ) : undefined
        }
      >
        {!importData && (
          <Empty
            description={
              <>
                <p>
                  Clique no botão abaixo para começar a fazer uma importação
                </p>
                <Button type="primary" onClick={modalAction.open}>
                  Importar orçamento
                </Button>
              </>
            }
          />
        )}

        {!!importData && (
          <>
            <Title style={{ marginTop: 0 }} level={3}>
              Dados gerais
            </Title>

            <WorkorderImportForm form={generalForm} />

            <Divider />

            <Group
              position="between"
              style={{ marginBottom: 24, flexWrap: 'wrap' }}
            >
              <Title style={{ margin: 0 }} level={3}>
                Dados do cliente
              </Title>

              <Button
                icon={<FaUserPlus />}
                children={
                  selectedCustomerId
                    ? 'Tirar seleção'
                    : 'Selecionar cliente já cadastrado'
                }
                type="primary"
                ghost
                onClick={() => {
                  if (selectedCustomerId) {
                    customerForm.resetFields();
                    customerForm.setFieldsValue({ address: {} });
                    setSelectedCustomerId(undefined);
                  } else {
                    modalCustomerAction.open();
                  }
                }}
              />
            </Group>

            <CustomerSimplifiedForm
              form={customerForm}
              address={
                selectedCustomerId
                  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (customerAddressForm as any)
                  : undefined
              }
              isEdit
              mainAddress={!hasCustomerRegistered}
              customerId={selectedCustomerId}
              disableAll={!!selectedCustomerId}
            />

            <Divider />

            <Title style={{ marginTop: 0 }} level={3}>
              Dados do veículo
            </Title>

            <VehicleFormWrapper
              key={keyDate}
              form={vehicleForm}
              removeAlert
              licensePlate={importData.data.vehicle?.licensePlate}
            />

            <Divider />

            <WorkorderImportItems form={generalForm} />

            <Divider />

            <WorkorderImportSummary items={generalFormValues?.items} />

            <Divider />

            <Row justify="end">
              <Col>
                <SaveButton
                  disabled={!hasItemsToImport}
                  onClick={async () => {
                    try {
                      await validateFields();
                      modalAreaAction.open();
                    } catch (error) {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      if ((error as any).values) {
                        message.warning('Preencha todos os campos.');
                      }
                    }
                  }}
                >
                  Confirmar importação
                </SaveButton>
              </Col>
            </Row>

            <Form form={generalForm}>
              <WorkorderImportAreaItemsModal
                open={modalAreaOpened}
                onCancel={modalAreaAction.close}
                onOk={handleSubmit}
                confirmLoading={workorderImportCreate.isLoading}
              />
            </Form>
          </>
        )}
      </DefaultRouteTabs>

      <WorkorderImportModal
        open={modalOpened}
        onCancel={modalAction.close}
        onImported={(data) => {
          resetFields();
          generalForm.setFieldValue('SSLocaliza', data.SSLocaliza);
          setImportData(data);
          modalAction.close();
        }}
      />

      <WorkorderImportInfoModal
        open={modalInfoOpened}
        workorders={importData?.data.thereIsWorkOrder}
        companyId={generalFormValues?.companyId}
        onSubmit={(info) => {
          setImportInfo(info);
          modalInfoAction.close();
        }}
      />

      <CustomersSelectedModal
        visible={modalCustomerOpened}
        handleVisible={modalCustomerAction.close}
        onCustomerSelect={async (customer) => {
          const general = await customerGeneral.mutateAsync(customer.id);
          const contacts = await customerContact.mutateAsync(customer.id);
          const addresses = await customerAddress.mutateAsync(customer.id);

          const customerPhone = getContactValue(contacts, 'PHONE');
          const customerEmail = getContactValue(contacts, 'EMAIL');

          customerForm.setFieldsValue({
            ...general,
            recommendation: { id: general.recommendationId },
            birthDate: moment(customer?.birthDate),
            phone: customerPhone
              ? applyMask(removeMask(customerPhone), phoneMask)
              : undefined,
            email: customerEmail,
            address:
              addresses?.data?.find(({ main }) => main) ||
              addresses?.data?.at(0),
          });

          setSelectedCustomerId(customer.id);
          modalCustomerAction.close();
        }}
        noSelection
      />
    </>
  );
};

export default WorkOrderImportPage;
